import { toast } from "react-toastify"

import { REQ } from "constants/api/requests"
import { IResponse, IResponsePayload } from "constants/interfaces"

import { Optional } from "types/common"

import useApi from "util/api"
import { formatRewardGameStatus } from "util/formatter"

import {
  Coin,
  EditUserBalancePayload,
  EditUserBalanceResponse,
  EditUserPayload,
  EditUserResponse,
  GetSingleUser,
  GetUserBetsResponse,
  GetUserCommentsResponse,
  GetUserReferralsResponse,
  GetUserRewardGamesResponse,
  GetUserTransactionsResponse,
  IUserService,
  Paginated,
  UpdateUserAffiliatePayload,
  UpdateUserAffiliatesBulkPayload,
  User,
  UserBonus,
  UserReload,
  UsersTableResponse,
  UserStatsTableEntry
} from "./type"

class UserService implements IUserService {
  public async getUser(): Promise<User | void> {
    const { data } = await useApi.get<IResponsePayload<User>>(REQ.USER.GET)
    if (data.success) {
      return data.response.payload
    }
  }

  public async editUser(id: string, payload: Partial<EditUserPayload>): Promise<EditUserResponse | void> {
    const { data } = await useApi.put<IResponse<EditUserResponse>>(REQ.ADMIN_DASHBOARD.USER.USER.replace(":id", id), payload)

    if (data?.success) {
      return data?.response
    }
  }

  public async createUser(payload: {
    username: string
    email: string
    password: string
    country: string
    phoneNumber: string
    siteLang?: string
    tenantId: string
  }): Promise<void> {
    try {
      const { data } = await useApi.post<IResponse<void>>(REQ.ADMIN_DASHBOARD.USER.CREATE, payload)

      if (!data.success) throw new Error("Failed to create user")
    } catch (error) {
      console.error("User creation failed:", error)
      throw error
    }
  }

  public async editUserBalance(id: string, payload: Partial<EditUserBalancePayload>): Promise<EditUserBalanceResponse[] | void> {
    const { data } = await useApi.put<IResponse<EditUserBalanceResponse[]>>(
      REQ.ADMIN_DASHBOARD.USER.USER_BALANCE.replace(":id", id),
      payload
    )

    if (data?.success) {
      return data?.response
    }
  }

  public async swapUserBalance(id: string, payload: { coin: Coin }): Promise<EditUserBalanceResponse[] | void> {
    const { data } = await useApi.post<IResponse<EditUserBalanceResponse[]>>(
      REQ.ADMIN_DASHBOARD.USER.SWAP_USER_BALANCE.replace(":id", id),
      payload
    )

    if (data?.success) {
      return data?.response
    }
  }

  public async getUsersTable(
    page: number,
    pageSize: number,
    id: number | undefined,
    { q, createdAt, country, tagIds }: { q: string; createdAt: (Date | null)[]; country: string | null; tagIds?: string[] },
    sortRow?: string,
    sortOrder?: string
  ): Promise<UsersTableResponse | void> {
    try {
      const params = {
        page,
        pageSize,
        q: q || null,
        createdAt: createdAt || null,
        country: country || null,
        tagIds: tagIds?.length ? tagIds : null,
        sortRow,
        sortOrder
      }

      const { data } = await useApi.get<IResponse<UsersTableResponse>>(REQ.ADMIN_DASHBOARD.USERS, { params })

      if (data?.success) {
        return data?.response
      }
    } catch (err: any) {
      toast.error(err?.message ?? err?.data?.message ?? "Failed to get users")
    }
  }

  public async getUserStatsTable(userId: string): Promise<UserStatsTableEntry[] | void> {
    try {
      const { data } = await useApi.get<IResponse<UserStatsTableEntry[]>>(REQ.ADMIN_DASHBOARD.USER.USER_STATS_TABLE.replace(":id", userId))
      if (data.success) {
        return data.response
      }
    } catch (error: any) {
      toast.error(error?.message ?? error?.data?.message ?? "Failed to fetch user stats")
    }
  }

  public async getAdminsTable(
    page: number,
    pageSize: number,
    id: number | undefined,
    {
      q,
      createdAt,
      country,
      role,
      activated
    }: { q: string; createdAt: (Date | null)[]; country: string | null; role?: string | null; activated?: boolean | null },
    sortRow?: string,
    sortOrder?: "asc" | "desc"
  ): Promise<UsersTableResponse | void> {
    try {
      const params = {
        page,
        pageSize,
        q: q ?? null,
        createdAt: createdAt ?? null,
        country: country ?? null,
        role: role ?? null,
        activated: activated ?? null,
        sortRow,
        sortOrder
      }

      const { data } = await useApi.get<IResponse<UsersTableResponse>>(REQ.ADMIN_DASHBOARD.ADMINS, { params })

      if (data?.success) return data.response
    } catch (err: any) {
      toast.error(err?.message ?? err?.data?.message ?? "Failed to get admin users")
    }
  }

  public async getAffiliatedUsersTable(
    page: number,
    pageSize: number,
    id: string,
    filter: { q: string; createdAt: (Date | null)[] } | undefined,
    sortRow?: string,
    sortOrder?: string
  ): Promise<UsersTableResponse | void> {
    try {
      const params = { page, pageSize, q: filter?.q || null, createdAt: filter?.createdAt || null, sortRow, sortOrder }
      const { data } = await useApi.get<IResponse<UsersTableResponse>>(REQ.ADMIN_DASHBOARD.AFFILIATED_USERS.replace(":userId", id), {
        params
      })

      if (data?.success) {
        return data?.response
      }
    } catch (err: any) {
      toast.error(err?.message ?? err?.data?.message ?? "Failed to get users")
    }
  }

  public async getUserById(id: string): Promise<GetSingleUser | void> {
    const { data } = await useApi.get<IResponse<GetSingleUser>>(REQ.ADMIN_DASHBOARD.USER.USER.replace(":id", id))

    if (data?.success) {
      return data?.response
    }
  }

  public async getUserBets(
    page: number,
    pageSize: number,
    id: number | undefined,
    { createdAt }: { createdAt?: (Date | null)[] },
    sortRow?: string,
    sortOrder?: string
  ): Promise<GetUserBetsResponse | void> {
    const params = {
      createdAt: createdAt || null,
      page,
      pageSize,
      sortRow,
      sortOrder
    }
    const { data } = await useApi.get<IResponse<GetUserBetsResponse>>(
      REQ.ADMIN_DASHBOARD.USER.USER_BETS.replace(":id", id ? id.toString() : ""),
      { params }
    )

    if (data?.success) {
      return data?.response
    }
  }

  public async getUserBetbyBets(
    page: number,
    pageSize: number,
    id: number | undefined,
    { createdAt }: { createdAt?: (Date | null)[] },
    sortRow?: string,
    sortOrder?: string
  ): Promise<GetUserBetsResponse | void> {
    const params = {
      createdAt: createdAt || null,
      page,
      pageSize,
      sortRow,
      sortOrder
    }
    const { data } = await useApi.get<IResponse<GetUserBetsResponse>>(
      REQ.ADMIN_DASHBOARD.USER.USER_BETBY_BETS.replace(":id", id ? id.toString() : ""),
      { params }
    )

    if (data?.success) {
      return data?.response
    }
  }

  public async getUserReferrals(
    page: number,
    pageSize: number,
    id: number | undefined,
    { createdAt }: { createdAt?: (Date | null)[] },
    sortRow?: string,
    sortOrder?: string
  ): Promise<GetUserReferralsResponse | void> {
    const params = {
      createdAt: createdAt || null,
      page,
      pageSize,
      sortRow,
      sortOrder
    }

    const { data } = await useApi.get<IResponse<GetUserReferralsResponse>>(
      REQ.ADMIN_DASHBOARD.USER.USER_REFERRALS.replace(":id", id ? id.toString() : ""),
      { params }
    )

    if (data?.success) {
      return data?.response
    }
  }

  public async getUserTransactions(
    page: number,
    pageSize: number,
    id: number | undefined,
    { type, status, createdAt }: { type: string; status: string; createdAt?: (Date | null)[] },
    sortRow?: string,
    sortOrder?: string
  ): Promise<GetUserTransactionsResponse | void> {
    const params = {
      page,
      pageSize,
      type: type || null,
      status: status || null,
      createdAt: createdAt || null,
      sortRow,
      sortOrder
    }

    const { data } = await useApi.get<IResponse<GetUserTransactionsResponse>>(
      REQ.ADMIN_DASHBOARD.USER.USER_TRANSACTIONS.replace(":id", id ? id.toString() : ""),
      { params }
    )

    if (data?.success) {
      return data?.response
    }
  }

  public async getUserRewardGames(
    page: number,
    pageSize: number,
    id: number | undefined,
    filters: any,
    sortRow?: string,
    sortOrder?: string
  ): Promise<GetUserRewardGamesResponse | void> {
    try {
      const params = {
        page,
        pageSize,
        sortRow,
        sortOrder
      }

      const { data } = await useApi.get<IResponse<GetUserRewardGamesResponse>>(
        REQ.ADMIN_DASHBOARD.USER.USER_REWARD_GAMES.replace(":id", id ? id.toString() : ""),
        { params }
      )

      if (data?.success) {
        data.response.data = data?.response?.data.map((rg: any) => ({ ...rg, status: formatRewardGameStatus(rg) }))
        return data?.response
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to get user reward games")
    }
  }

  public async addUserRewardGames(id: string, gameIdentifier: string): Promise<any> {
    const { data } = await useApi.post(REQ.ADMIN_DASHBOARD.USER.USER_ADD_REWARD_GAME, { id, gameIdentifier })

    if (data?.success) {
      return data?.response
    }
  }

  public async deleteUserRewardGames(id: string, gameRewardId: string): Promise<any> {
    const { data } = await useApi.delete<IResponse<any>>(REQ.ADMIN_DASHBOARD.USER.USER_DELETE_REWARD_GAME, {
      data: { id: id, gameRewardId: gameRewardId }
    })

    if (data?.success) {
      return data?.response
    }
  }

  public async getUserBonuses(
    page: number,
    pageSize: number,
    id: string | undefined,
    filters: any,
    sortRow?: string,
    sortOrder?: string
  ): Promise<Optional<Paginated<UserBonus[]>>> {
    try {
      const { status, createdAt } = filters ?? {}
      const params = { page, pageSize, sortRow, sortOrder, status, createdAt }
      const { data } = await useApi.get<IResponse<GetUserRewardGamesResponse>>(
        REQ.ADMIN_DASHBOARD.USER.GET_BONUS.replace(":id", id?.toString() ?? ""),
        { params }
      )

      if (data?.success) {
        return data?.response
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to get user bonuses")
    }
  }

  public async getUserTotalBonus(id: string): Promise<any> {
    try {
      const { data } = await useApi.get<IResponse<any>>(REQ.ADMIN_DASHBOARD.USER.GET_TOTAL_BONUS.replace(":id", id?.toString()))

      if (data?.success) {
        return data?.response
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to get user bonus")
    }
  }

  public async getAcitivtyLogs(
    page: number,
    pageSize: number,
    id: string | undefined,
    filters: any,
    sortRow?: string,
    sortOrder?: string
  ): Promise<Optional<Paginated<UserBonus[]>>> {
    try {
      const { status, createdAt } = filters ?? {}
      const params = { page, pageSize, sortRow, sortOrder, status, createdAt }
      const { data } = await useApi.get<IResponse<any>>(REQ.ADMIN_DASHBOARD.USER.ACCOUNT_ACTIVITIES.replace(":id", id?.toString() ?? ""), {
        params
      })

      if (data?.success) {
        return data?.response
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Failed")
    }
  }

  public async cancelUserBonus(id: string, userId: string): Promise<any> {
    const { data } = await useApi.post<IResponse<any>>(REQ.ADMIN_DASHBOARD.USER.CANCEL_BONUS, {
      id,
      userId
    })

    if (data?.success) {
      return data?.response
    }
  }

  public async claimCustomBonus(payload: any): Promise<any> {
    const { data } = await useApi.post<IResponse<any>>(REQ.ADMIN_DASHBOARD.USER.CLAIM_CUSTOM_BONUS, payload)

    if (data?.success) {
      return data?.response
    }
  }

  public async assignReload(payload: { userId: string; amount: number; days: number }): Promise<UserReload | void> {
    try {
      const { data } = await useApi.post<IResponse<UserReload>>(REQ.ADMIN_DASHBOARD.USER.ASSIGN_RELOAD, payload)

      if (data?.success) {
        return data?.response
      }
    } catch (err: any) {
      toast.error(err?.data?.message ?? "Failed to assign reload")
    }
  }

  public async getUserReloads(userId: string): Promise<UserReload[]> {
    try {
      const { data } = await useApi.get<IResponse<UserReload[]>>(REQ.ADMIN_DASHBOARD.USER.GET_USER_RELOADS.replace(":id", userId))

      if (data?.success) {
        return data?.response ?? []
      }
    } catch (err: any) {
      toast.error(err?.data?.message ?? err?.message ?? "Failed to load reloads")
    }
    return []
  }

  public async cancelReload(reloadId: string): Promise<boolean> {
    try {
      const { data } = await useApi.delete<IResponse<{ result: string }>>(REQ.ADMIN_DASHBOARD.USER.CANCEL_RELOAD.replace(":id", reloadId))

      if (data?.success) {
        return true
      }
    } catch (err: any) {
      toast.error(err?.data?.message ?? err?.message ?? "Failed to cancel reload")
    }
    return false
  }

  public async addComment(userId: string, comment: string, category: string): Promise<any> {
    const { data } = await useApi.post<IResponse<any>>(REQ.ADMIN_DASHBOARD.USER.ADD_COMMENTS, {
      userId,
      comment,
      category
    })

    if (data?.success) {
      return data?.response
    }
  }

  public async getUserComments(
    page: number,
    pageSize: number,
    id: number | undefined,
    { category, createdAt }: { category: string; createdAt: (Date | null)[] },
    sortRow?: string,
    sortOrder?: string
  ): Promise<GetUserCommentsResponse | void> {
    const params = {
      page,
      pageSize,
      category: category || null,
      createdAt: createdAt || null,
      sortRow,
      sortOrder
    }

    const { data } = await useApi.get<IResponse<GetUserCommentsResponse>>(
      REQ.ADMIN_DASHBOARD.USER.USER_COMMENTS.replace(":id", id ? id.toString() : ""),
      { params }
    )

    if (data?.success) {
      return data?.response
    }
  }

  public async updateUserAffiliate(userId: string, affiliateId: string, payload: UpdateUserAffiliatePayload) {
    const url = REQ.ADMIN_DASHBOARD.USER.UPDATE_AFFILIATE.replace(":id", userId).replace(":affiliateId", affiliateId)

    const { data } = await useApi.patch<IResponse<any>>(url, payload)
    if (data?.success) return data?.response
  }

  public async updateUserAffiliatesBulk(userId: string, payload: UpdateUserAffiliatesBulkPayload) {
    const url = REQ.ADMIN_DASHBOARD.USER.UPDATE_AFFILIATES_BULK.replace(":id", userId)
    const { data } = await useApi.patch<IResponse<any>>(url, payload)
    if (data?.success) return data?.response
  }

  public async releaseAffiliateNgrEarnings(userId: string, payload?: { to?: string }) {
    const url = REQ.ADMIN_DASHBOARD.USER.RELEASE_AFFILIATE_NGR_EARNINGS.replace(":id", userId)
    const { data } = await useApi.post<IResponse<any>>(url, payload ?? {})
    if (data?.success) return data?.response
  }

  public async disableUserTwoFA(userId: string): Promise<void> {
    const url = REQ.ADMIN_DASHBOARD.USER.DISABLE_MFA.replace(":id", userId)
    const { data } = await useApi.patch<IResponse<void>>(url)
    if (data?.success) return data?.response
  }

  public async sendUserMessage(userId: string, payload: { subject: string; message: string }): Promise<boolean> {
    try {
      const url = REQ.ADMIN_DASHBOARD.USER.SEND_MESSAGE.replace(":id", userId)
      const { data } = await useApi.post<IResponse<void>>(url, payload)

      if (data?.success) {
        return true
      }
    } catch (err: any) {
      toast.error(err?.data?.message ?? err?.message ?? "Failed to send message")
    }
    return false
  }

  public async getCustomHalls(userId: string): Promise<{ customHalls: string[] } | void> {
    const url = REQ.ADMIN_DASHBOARD.CUSTOM_HALLS.GET.replace(":userId", userId)
    const { data } = await useApi.get<IResponse<{ customHalls: string[] }>>(url)
    if (data?.success) return data?.response
  }

  public async addCustomHall(userId: string, provider: string): Promise<{ customHalls: string[] } | void> {
    const url = REQ.ADMIN_DASHBOARD.CUSTOM_HALLS.ADD.replace(":userId", userId).replace(":provider", provider)
    const { data } = await useApi.post<IResponse<{ customHalls: string[] }>>(url)
    if (data?.success) return data?.response
  }

  public async removeCustomHall(userId: string, provider: string): Promise<{ customHalls: string[] } | void> {
    const url = REQ.ADMIN_DASHBOARD.CUSTOM_HALLS.REMOVE.replace(":userId", userId).replace(":provider", provider)
    const { data } = await useApi.delete<IResponse<{ customHalls: string[] }>>(url)
    if (data?.success) return data?.response
  }

  public async updatePersonalProfileMisc(
    userId: string,
    payload: { dark_streamer?: boolean; sv_streamer?: boolean }
  ): Promise<{ misc: any } | void> {
    const url = REQ.ADMIN_DASHBOARD.USER.UPDATE_PERSONAL_PROFILE_MISC.replace(":id", userId)
    const { data } = await useApi.patch<IResponse<{ misc: any }>>(url, payload)
    if (data?.success) return data?.response
  }

  public async updateUserAccountPolicy(
    userId: string,
    payload: {
      is_c2_streamer?: boolean
      is_dark_streamer?: boolean
      is_sv_streamer?: boolean
      exclude_dashboard_ggr?: boolean
      exclude_c2_ggr?: boolean
      exclude_dark_ggr?: boolean
      disable_withdrawals?: boolean
      exclude_from_promotions?: boolean
      sync_policy?: boolean
    }
  ): Promise<{ policy: any } | void> {
    const url = REQ.ADMIN_DASHBOARD.USER.UPDATE_ACCOUNT_POLICY.replace(":id", userId)
    const { data } = await useApi.patch<IResponse<{ policy: any }>>(url, payload)
    if (data?.success) return data?.response
  }

  public async startTwoFactorSetup(): Promise<{
    qr: string
    manualCode: string
  } | void> {
    try {
      const { data } = await useApi.get<IResponse<{ qr: string; manualCode: string }>>(REQ.USER.MFA.SETUP)

      if (data?.success) {
        return data.response
      }
    } catch (error) {
      toast.error("Failed to start 2FA setup")
    }
  }

  public async enableTwoFactor(code: string): Promise<string[] | void> {
    try {
      const { data } = await useApi.post<IResponse<string[]>>(REQ.USER.MFA.ENABLE, { two_fa_code: code })

      if (data?.success) {
        return data.response
      }
    } catch (error) {
      toast.error("Failed to enable 2FA")
    }
  }

  public async disableTwoFactor(code: string): Promise<boolean | void> {
    try {
      const { data } = await useApi.post<IResponse<{ disabled: boolean }>>(REQ.USER.MFA.REMOVE, { two_fa_code: code })

      return !!(data?.success && data.response.disabled)
    } catch (error) {
      toast.error("Failed to disable 2FA")
    }
  }

  public async removeTwoFactorByBackup(backupCode: string): Promise<boolean | void> {
    try {
      const { data } = await useApi.post<IResponse<{ disabled: boolean }>>(REQ.AUTH.MFA_REMOVE_BY_BACKUP, { recovery_code: backupCode })

      return !!(data?.success && data.response.disabled)
    } catch (error) {
      toast.error("Failed to disable 2FA with backup code")
    }
  }
}

export const userService = new UserService()
